export type ArticleBlock =
  | { type: "h2"; text: string }
  | { type: "h3"; text: string }
  | { type: "p"; text: string }
  | { type: "quote"; text: string }
  | { type: "list"; items: string[] };

export type Article = {
  slug: string;
  title: string;
  excerpt: string;
  categoryId: string;
  cover: string;
  author: string;
  authorRole: string;
  date: string;
  readTime: string;
  views: number;
  comments: number;
  heat: number;
  featured: boolean;
  tags: string[];
  content: ArticleBlock[];
};

export const categories = [
  { id: "all", name: "全部瓜线", icon: "ri-layout-grid-line", desc: "所有正在发酵的热门话题" },
  { id: "star", name: "明星动向", icon: "ri-user-star-line", desc: "艺人行程、动态与合作消息" },
  { id: "variety", name: "综艺现场", icon: "ri-mic-line", desc: "录制路透与节目名场面" },
  { id: "film", name: "影视热点", icon: "ri-film-line", desc: "剧集、电影与口碑讨论" },
  { id: "insider", name: "行业揭秘", icon: "ri-lock-unlock-line", desc: "读懂娱乐工业的运作逻辑" },
  { id: "etiquette", name: "围观礼仪", icon: "ri-shield-star-line", desc: "理性吃瓜的正确打开方式" },
  { id: "twist", name: "反转复盘", icon: "ri-arrow-go-back-line", desc: "事件时间线与后续追踪" },
];

export const articles: Article[] = [
  {
    slug: "how-to-watch-drama-correctly",
    title: "围观姿势第一课：把「吃瓜」变成一场有秩序的讨论",
    excerpt:
      "热搜只给了结论，真正的瓜线藏在时间线里。这篇整理了一套从信息采集到判断落地的完整围观流程，帮你从被动刷屏切换到主动梳理。",
    categoryId: "etiquette",
    cover:
      "https://readdy.ai/api/search-image?query=Warm%20amber%20and%20terracotta%20flat%20illustration%20of%20a%20timeline%20board%20with%20sticky%20notes%20and%20connecting%20lines%2C%20abstract%20editorial%20style%2C%20soft%20paper%20texture%2C%20no%20text%2C%20minimal%20geometric%20shapes%2C%20harmonious%20warm%20palette&width=1200&height=800&seq=mock-article-01&orientation=landscape",
    author: "瓜田老编",
    authorRole: "内容主理人",
    date: "2026-09-08",
    readTime: "7 分钟",
    views: 128600,
    comments: 942,
    heat: 98,
    featured: true,
    tags: ["围观礼仪", "时间线", "信息判断"],
    content: [
      { type: "p", text: "几乎所有人的吃瓜路径都是一样的：刷到一条热搜，点进去看两条评论，然后被下一条热搜带走。半小时过去，脑子里只剩下几个模糊的名字，却说不清事情到底发生了什么。问题不在于信息太少，而在于我们没有把它组织起来。" },
      { type: "h2", text: "第一步：先固定时间线，再去看观点" },
      { type: "p", text: "任何一条瓜线都有一条真实的时间轴。把已知的公开节点按时间排好，是最省力的梳理方式。当你看不清时间线时，最容易把两件不相关的事强行拼成一个因果故事。" },
      { type: "list", items: ["记录事件首次公开出现的日期与平台", "标记每一次关键回应发生的时间", "区分「当事方表态」与「第三方转述」", "把未证实的说法单独列一栏，不要混进主线"] },
      { type: "h2", text: "第二步：给信息源分级" },
      { type: "p", text: "围观最容易踩的坑，是把转述当成原文。一条消息被转发五次之后，中间任何一次都可能被加上情绪与猜测。养成习惯：先找最接近源头的那一版，再回头对比被加工过的版本。" },
      { type: "quote", text: "看到「据知情人士透露」时，先问一句：这位知情人士是谁，他为什么恰好知道。" },
      { type: "h2", text: "第三步：把情绪留在评论区，把事实带回主线" },
      { type: "p", text: "评论区是情绪浓度最高的地方，也是最容易上头的区域。它适合感受氛围，不适合当作事实依据。建议把评论区当成「民意采样」，而把正文与官方回应当成「事实主干」。" },
      { type: "h2", text: "第四步：认真对待每一次反转" },
      { type: "p", text: "反转不是打脸，而是信息补齐后的正常修正。真正有价值的复盘，是记录下自己当时为什么会相信旧的版本，这样下一次面对类似的信息时，判断会更快。" },
      { type: "p", text: "围观不是围观本身，而是一种在信息洪流里保持清醒的练习。姿势对了，你不仅吃得明白，还吃得舒服。" },
    ],
  },
  {
    slug: "variety-show-roadmap",
    title: "综艺录制路透满天飞，哪些细节真的值得追",
    excerpt:
      "一档综艺从路透到播出，中间隔着剪辑、补录与后期。看懂录制规则，你就能分辨哪些路透是真信号，哪些只是现场气氛组的一时兴起。",
    categoryId: "variety",
    cover:
      "https://readdy.ai/api/search-image?query=Warm%20flat%20illustration%20of%20a%20television%20studio%20stage%20with%20spotlights%20and%20geometric%20seating%2C%20abstract%20editorial%20style%2C%20amber%20and%20coral%20tones%2C%20soft%20paper%20texture%2C%20no%20text%2C%20clean%20composition&width=1200&height=800&seq=mock-article-02&orientation=landscape",
    author: "片场小喇叭",
    authorRole: "综艺观察员",
    date: "2026-09-06",
    readTime: "6 分钟",
    views: 94300,
    comments: 613,
    heat: 93,
    featured: true,
    tags: ["综艺", "路透", "录制"],
    content: [
      { type: "p", text: "每逢一档热门综艺开录，社交平台上就会冒出一批现场描述。它们细节丰富、情绪饱满，看起来非常有说服力。但录影棚里的三小时，不等于电视机前的一小时。" },
      { type: "h2", text: "路透为什么常常和正片不一样" },
      { type: "p", text: "一档节目的最终形态，是素材量、剪辑节奏和播出时长三者博弈的结果。现场的高光片段可能因为节奏问题被删掉，平淡的环节也可能因为后期包装变成名场面。" },
      { type: "list", items: ["现场录制顺序不等于播出顺序", "补录与二次采访会改变叙事重心", "同一环节可能反复录制多个版本", "观众看到的是成片，不是现场流水账"] },
      { type: "h2", text: "真正值得记录的三个信号" },
      { type: "p", text: "与其追每一条路透，不如盯住少数几个结构性信号：嘉宾阵容的确认、环节设定的变化、以及节目组对节奏的公开表达。这三者往往决定了整季节目的走向。" },
      { type: "h3", text: "其一：阵容组合的化学反应" },
      { type: "p", text: "固定班底与飞行嘉宾的比例，直接决定了节目是稳还是有爆点。稳定的搭档负责安全区，新面孔负责意外感。" },
      { type: "h3", text: "其二：环节的迭代方向" },
      { type: "p", text: "如果一个节目从对抗类转向纪实类，说明制作方在调整受众预期，这类变化通常比单条路透更有信息量。" },
      { type: "h2", text: "给围观者的建议" },
      { type: "p", text: "把路透当参考，把正片当结论。看到夸张描述时，先记下时间点，等节目播出再回头对照，你会对综艺制作多一层理解，也少一分被带节奏的可能。" },
    ],
  },
  {
    slug: "drama-twist-timeline-review",
    title: "一次典型的「反转」是如何发生的：完整时间线复盘",
    excerpt:
      "从最初的爆料，到中途的沉默，再到最后的澄清，这条瓜线只用了十一天。我们把它拆成四个阶段，看看情绪是怎样一步步盖过事实的。",
    categoryId: "twist",
    cover:
      "https://readdy.ai/api/search-image?query=Warm%20abstract%20illustration%20of%20a%20spiral%20path%20with%20numbered%20milestone%20dots%2C%20terracotta%20and%20amber%20paper%20collage%20style%2C%20editorial%20minimal%20composition%2C%20no%20text%2C%20soft%20shadows&width=1200&height=800&seq=mock-article-03&orientation=landscape",
    author: "时间线整理员",
    authorRole: "内容编辑",
    date: "2026-09-04",
    readTime: "8 分钟",
    views: 156200,
    comments: 1287,
    heat: 96,
    featured: true,
    tags: ["反转", "复盘", "时间线"],
    content: [
      { type: "p", text: "我们复盘一条完整的瓜线，不是为了追究谁对谁错，而是想看清一件更值得研究的事：一条从模糊开始的消息，是怎样在十天里变成所有人都深信不疑的「事实」的。" },
      { type: "h2", text: "阶段一：模糊的起点" },
      { type: "p", text: "最初的信息往往只有一张模糊截图和一句「据说」。它的特点是无法核实，但又足够有想象空间，于是被迅速转发。" },
      { type: "h2", text: "阶段二：情绪开始接力" },
      { type: "p", text: "当第一批人开始代入自己的情绪后，信息就完成了从「听说」到「我觉得」的跃迁。此时讨论的重心从「发生了什么」转移到「这说明了什么」。" },
      { type: "quote", text: "当问题从事实变成立场，讨论就不再需要证据了。" },
      { type: "h2", text: "阶段三：沉默被解读成默认" },
      { type: "p", text: "当事方的沉默，本来只是一种常见的处理方式，但在情绪已经就位的情况下，沉默会被自动补全成认罪。" },
      { type: "h2", text: "阶段四：澄清发布后的二次传播" },
      { type: "p", text: "即便澄清发布，热度也不会立刻消退。真正需要时间的是人的认知更新，而不是消息本身的传播速度。" },
      { type: "list", items: ["保留原始时间点，方便日后对照", "区分「信息」与「对信息的解读」", "对沉默保持中立，不自动补全动机", "澄清发布后，给它同等的传播耐心"] },
      { type: "p", text: "一条瓜线结束，但围观的方法论可以留下来。下次再遇到类似的节奏，你会发现自己冷静得比上一次更早。" },
    ],
  },
  {
    slug: "entertainment-industry-basics",
    title: "看懂娱乐工业：一个项目从立项到上线要经过什么",
    excerpt:
      "很多争议之所以反复出现，是因为大众不了解行业的基本流程。理解了立项、拍摄、宣发三个环节，你会发现相当一部分「八卦」其实只是正常的商业决策。",
    categoryId: "insider",
    cover:
      "https://readdy.ai/api/search-image?query=Warm%20minimal%20illustration%20of%20connected%20geometric%20blocks%20forming%20a%20production%20pipeline%2C%20terracotta%20and%20amber%20tones%2C%20paper%20texture%2C%20editorial%20abstract%20style%2C%20no%20text&width=1200&height=800&seq=mock-article-04&orientation=landscape",
    author: "行业观察笔记",
    authorRole: "特约撰稿",
    date: "2026-09-02",
    readTime: "9 分钟",
    views: 76800,
    comments: 402,
    heat: 88,
    featured: false,
    tags: ["行业", "科普", "流程"],
    content: [
      { type: "p", text: "娱乐行业的很多讨论，最后都会落到一个误解上：把商业流程当成个人恩怨。理解流程之后，许多看起来戏剧化的决定，其实都有非常朴素的解释。" },
      { type: "h2", text: "立项：资源匹配比热情更重要" },
      { type: "p", text: "一个项目能否启动，取决于剧本、主创、平台档期与预算四者是否同时成立。任一环节错位，项目就会进入漫长的等待。" },
      { type: "h2", text: "拍摄：妥协是常态" },
      { type: "p", text: "天气、场地、演员档期、预算，任何一个变量的变动都会影响成片。观众最终看到的版本，是无数次取舍之后的结果。" },
      { type: "list", items: ["立项阶段决定题材与定位", "拍摄阶段决定完成度与质量下限", "宣发阶段决定它被多少人真正看到", "播出后的口碑会反向影响后续资源"] },
      { type: "h2", text: "宣发：节奏本身就是内容" },
      { type: "p", text: "什么时候放出物料、什么时候接受采访、什么时候保持安静，都是经过设计的。这也解释了为什么有些消息会「恰好」在同一周出现。" },
      { type: "h2", text: "为什么要懂这些" },
      { type: "p", text: "懂得流程，不是为了替谁辩解，而是为了在信息不完整时，给自己留出判断的余地。这是围观者最实用的护身符。" },
    ],
  },
  {
    slug: "film-word-of-mouth-guide",
    title: "影视口碑怎么形成的：第一周为什么最关键",
    excerpt:
      "一部作品上映后的七天，几乎决定了它的最终评价。我们梳理了口碑发酵的四个节点，帮你在第一时间读懂真实的观众反馈。",
    categoryId: "film",
    cover:
      "https://readdy.ai/api/search-image?query=Warm%20abstract%20illustration%20of%20floating%20film%20frames%20and%20star%20shapes%20in%20amber%20and%20coral%20tones%2C%20paper%20collage%20editorial%20style%2C%20minimal%20composition%2C%20no%20text&width=1200&height=800&seq=mock-article-05&orientation=landscape",
    author: "银幕边角料",
    authorRole: "影评撰稿",
    date: "2026-08-30",
    readTime: "6 分钟",
    views: 68100,
    comments: 355,
    heat: 84,
    featured: false,
    tags: ["影视", "口碑", "观众反馈"],
    content: [
      { type: "p", text: "上映首周的讨论质量，往往比首日票房更能预示一部作品的长线走势。原因很简单：首日靠宣发，首周靠真实观众。" },
      { type: "h2", text: "节点一：首日的高开由宣发决定" },
      { type: "p", text: "首日的热度、预售和排片，很大程度上是宣发投入的直接体现，它反映的是期待值，而不是满意度。" },
      { type: "h2", text: "节点二：第三天的真实反馈开始浮现" },
      { type: "p", text: "当普通观众入场之后，讨论的话题会从明星转向情节、节奏与完成度。这时出现的关键词，通常最接近作品本身。" },
      { type: "h2", text: "节点三：第五天的口碑分层" },
      { type: "p", text: "口碑不会统一，它会分层。找到属于你所在圈层的那一层评价，比看总评分更有参考价值。" },
      { type: "list", items: ["关注具体情节的讨论，而不只是情绪表达", "对比不同平台的评论结构差异", "留意反复出现的关键词，它们往往指向真实问题", "给自己留出一周再下结论"] },
      { type: "h2", text: "节点四：第七天的长线定调" },
      { type: "p", text: "一周之后，作品会找到自己稳定的观众群体。此时的热度不再剧烈波动，评价也趋于稳定，这才是最值得参考的时刻。" },
    ],
  },
  {
    slug: "chat-room-etiquette",
    title: "聊天室里蹲后续：几条能让讨论更长久的规矩",
    excerpt:
      "聊天室最怕的不是没人说话，而是聊着聊着就吵散了。这六条不成文的规矩，是让一个吃瓜群长期活跃的真正原因。",
    categoryId: "etiquette",
    cover:
      "https://readdy.ai/api/search-image?query=Warm%20abstract%20illustration%20of%20overlapping%20speech%20bubbles%20in%20amber%20and%20terracotta%20paper%20style%2C%20editorial%20minimal%2C%20soft%20texture%2C%20no%20text%2C%20balanced%20composition&width=1200&height=800&seq=mock-article-06&orientation=landscape",
    author: "围观姿势研究员",
    authorRole: "社区运营",
    date: "2026-08-27",
    readTime: "5 分钟",
    views: 54200,
    comments: 488,
    heat: 81,
    featured: false,
    tags: ["聊天室", "群聊", "礼仪"],
    content: [
      { type: "p", text: "一个能长期活跃的聊天室，靠的从来不是人多，而是有一套所有人都默认遵守的规矩。规矩越清楚，讨论越轻松。" },
      { type: "h2", text: "一：先说事实，再说看法" },
      { type: "p", text: "把「我看到的消息是」和「我觉得这意味着」分开说，能避免一大半无谓的争执。" },
      { type: "h2", text: "二：不带节奏，也不追着问立场" },
      { type: "p", text: "在群里点名让别人表态，是最快让气氛变差的方式。你可以表达喜欢，但不必要求所有人认同。" },
      { type: "h2", text: "三：不搬运未经确认的截图" },
      { type: "p", text: "转发截图之前，先问自己能不能找到原始来源。找不到就先放在一边。" },
      { type: "h2", text: "四：允许别人中途离场" },
      { type: "p", text: "不是所有人都有精力追完每一条瓜线。有人退出讨论，不代表立场，只代表时间有限。" },
      { type: "list", items: ["对事不对人，评价内容而非身份", "不刷屏，给其他人留出表达空间", "有分歧时先确认双方说的是不是同一件事", "热度过高时,管理员有权暂时降温"] },
      { type: "p", text: "规矩的目的不是限制表达，而是让表达被听见。做到了这一点，瓜田才能一直热闹下去。" },
    ],
  },
  {
    slug: "star-rumor-reading-guide",
    title: "合作、代言、行程：明星动态里哪些是真信号",
    excerpt:
      "艺人的公开动态数量庞大，但真正有意义的变化其实只有几类。学会区分「常规更新」与「结构性变化」，判断会准确很多。",
    categoryId: "star",
    cover:
      "https://readdy.ai/api/search-image?query=Warm%20abstract%20illustration%20of%20a%20calendar%20grid%20with%20highlighted%20dots%20and%20arrows%2C%20amber%20and%20terracotta%20paper%20collage%20editorial%20style%2C%20minimal%20no%20text&width=1200&height=800&seq=mock-article-07&orientation=landscape",
    author: "动态追踪组",
    authorRole: "内容编辑",
    date: "2026-08-24",
    readTime: "6 分钟",
    views: 88900,
    comments: 561,
    heat: 90,
    featured: false,
    tags: ["明星动向", "代言", "行程"],
    content: [
      { type: "p", text: "关注艺人动态的人，最常遇到的困惑是：消息太多，分不清哪条重要。其实真正值得记录的信号并不多，关键在分类。" },
      { type: "h2", text: "常规更新：维持热度，不必过度解读" },
      { type: "p", text: "日常营业照、节日祝福、转发合作方内容，都属于维持曝光的基础动作，它们的出现频率稳定，参考价值有限。" },
      { type: "h2", text: "结构性信号一：合作对象的层级变化" },
      { type: "p", text: "当合作品牌的类型或量级发生明显变化时，往往意味着团队在调整路线，这比单条动态更有信息量。" },
      { type: "h2", text: "结构性信号二：行程密度的变化" },
      { type: "p", text: "行程突然变密或变空，通常是新项目启动或旧项目收尾的前兆。把行程表按周排开，趋势会非常直观。" },
      { type: "list", items: ["记录合作方类型的变化趋势", "对比不同季度的公开行程密度", "关注团队官方账号的招聘与调整", "把粉丝传闻与官方信息分开归档"] },
      { type: "h2", text: "结构性信号三：公开表达的措辞变化" },
      { type: "p", text: "措辞的变化往往先于行动。留意采访中反复出现的词，它们通常反映了下一阶段的重点。" },
    ],
  },
  {
    slug: "hot-search-mechanism",
    title: "热搜是怎么形成的：从点击到榜单的完整链路",
    excerpt:
      "理解热度的计算逻辑，才能不被热度牵着走。这篇文章用通俗的方式，讲清楚一条话题从出现到登上榜单的过程。",
    categoryId: "insider",
    cover:
      "https://readdy.ai/api/search-image?query=Warm%20abstract%20illustration%20of%20ascending%20bar%20shapes%20and%20rising%20arrows%20in%20amber%20and%20coral%20paper%20style%2C%20editorial%20minimal%20composition%2C%20no%20text&width=1200&height=800&seq=mock-article-08&orientation=landscape",
    author: "数据观察员",
    authorRole: "特约撰稿",
    date: "2026-08-21",
    readTime: "7 分钟",
    views: 61200,
    comments: 371,
    heat: 86,
    featured: false,
    tags: ["热搜", "机制", "数据"],
    content: [
      { type: "p", text: "热搜是很多人获取信息的入口，但它本质上是一个经过计算与排序的榜单，而不是事实排行榜。" },
      { type: "h2", text: "热度由哪些行为构成" },
      { type: "p", text: "点击、讨论、转发、停留时长，都会参与热度的计算。不同平台的权重不同，因此同一个话题在不同榜单上的位置也会不一样。" },
      { type: "h2", text: "为什么有些话题上得快、掉得也快" },
      { type: "p", text: "瞬时涌入的讨论能快速推高排名，但如果没有持续的二次讨论，热度会很快回落。这解释了为什么有些话题只停留了很短的时间。" },
      { type: "list", items: ["榜单排名反映关注度，不直接等于重要性", "带话题讨论比单纯浏览更有推动力", "热度下降不代表事件结束", "多平台对比能减少单一算法的偏差"] },
      { type: "h2", text: "如何更理性地使用热搜" },
      { type: "p", text: "把热搜当成线索入口，而不是结论来源。看到感兴趣的话题，回到可核实的信息里再看一遍，你的判断会稳定得多。" },
    ],
  },
  {
    slug: "fan-community-health",
    title: "让讨论区长期健康的四件小事",
    excerpt:
      "一个内容社区能否长久，取决于最基础的四件事：规则是否清楚、申诉是否顺畅、争议是否有出口、以及新人的第一条发言有没有被认真回应。",
    categoryId: "etiquette",
    cover:
      "https://readdy.ai/api/search-image?query=Warm%20abstract%20illustration%20of%20interlocking%20rounded%20shapes%20forming%20a%20circle%20of%20people%2C%20amber%20and%20terracotta%20paper%20collage%2C%20editorial%20minimal%2C%20no%20text&width=1200&height=800&seq=mock-article-09&orientation=landscape",
    author: "社区共建组",
    authorRole: "社区运营",
    date: "2026-08-18",
    readTime: "5 分钟",
    views: 43600,
    comments: 292,
    heat: 79,
    featured: false,
    tags: ["社区", "规则", "氛围"],
    content: [
      { type: "p", text: "很多社区并不是被某一次冲突击垮的，而是在一次次小事没被处理好之后，慢慢失去信任。" },
      { type: "h2", text: "一：规则要写在明面上" },
      { type: "p", text: "规则越具体，执行越少争议。与其写「请文明发言」，不如写清楚哪些内容会被折叠、哪些行为会被限制。" },
      { type: "h2", text: "二：给争议留一个出口" },
      { type: "p", text: "争论不可避免，但需要一个专门的空间让双方把话说完，而不是在公共版面反复拉扯。" },
      { type: "h2", text: "三：让申诉是可完成的" },
      { type: "p", text: "如果申诉流程复杂到没人愿意走完，那它等于不存在。简单、可追踪、有回应，是最低标准。" },
      { type: "list", items: ["规则公开且可被引用", "争议有独立讨论区", "申诉流程不超过三步", "认真回应新人的第一条发言"] },
      { type: "h2", text: "四：认真对待第一次发言" },
      { type: "p", text: "新人愿不愿意留下，往往取决于第一条发言有没有被回应。一个友善的回复，胜过十条标语。" },
    ],
  },
];

export const getArticleBySlug = (slug: string): Article | undefined =>
  articles.find((item) => item.slug === slug);

export const getCategoryName = (id: string): string =>
  categories.find((item) => item.id === id)?.name ?? "瓜线";

export const getRelatedArticles = (slug: string, limit = 3): Article[] =>
  articles.filter((item) => item.slug !== slug).slice(0, limit);

export const hotArticles = [...articles].sort((a, b) => b.heat - a.heat);